import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LibraryPicker } from './LibraryPicker';
import styles from './LibkeyLibrarySection.module.scss';
import LibkeyLogo from 'assets/images/libkey/libkey-logo.png';
import {
  LibkeyLibrary,
  LibkeySelection,
  fetchLibkeyLibraries,
  saveLibkeySelection,
} from 'query/study/LibKey/Libraries';
import { Heading, Link } from 'components/core';
import { Text } from 'components/core/Typography';
import { Inline, Stack } from 'components/layout';

export type LibrariesState =
  | { status: 'idle' }
  | { status: 'loading' }
  | {
      status: 'loaded';
      libraries: LibkeyLibrary[];
      labelByLibrary: Map<number, string>;
      libraryByLabel: Map<string, LibkeyLibrary>;
    }
  | { status: 'empty' }
  | { status: 'error' };

// LibKey returns multiple libraries that share a name (different campuses /
// branches with the same parent institution). ComboBox uses the display
// string as its React key, so we must disambiguate duplicates.
const buildLabels = (libraries: LibkeyLibrary[]) => {
  const counts = new Map<string, number>();
  libraries.forEach((library) => {
    counts.set(library.name, (counts.get(library.name) ?? 0) + 1);
  });
  const labelByLibrary = new Map<number, string>();
  const libraryByLabel = new Map<string, LibkeyLibrary>();
  libraries.forEach((library) => {
    const base = library.name;
    const label =
      (counts.get(base) ?? 0) > 1 ? `${base} (#${library.id})` : base;
    labelByLibrary.set(library.id, label);
    libraryByLabel.set(label, library);
  });
  return { labelByLibrary, libraryByLabel };
};

export type LibkeyLibrarySectionProps = {
  initialSelection: LibkeySelection | null;
  fetchLibraries?: typeof fetchLibkeyLibraries;
  saveSelection?: typeof saveLibkeySelection;
};

const LibkeyLibrarySection = ({
  initialSelection,
  fetchLibraries = fetchLibkeyLibraries,
  saveSelection = saveLibkeySelection,
}: LibkeyLibrarySectionProps) => {
  const [librariesState, setLibrariesState] = useState<LibrariesState>({
    status: 'idle',
  });

  // librariesStateRef lets loadLibraries read the latest state without
  // depending on it (which would re-create the callback and re-bind the
  // ComboBox's focus handler).
  const librariesStateRef = useRef(librariesState);
  useEffect(() => {
    librariesStateRef.current = librariesState;
  }, [librariesState]);

  // Tracks whether the component is still mounted so an in-flight fetch
  // that resolves after unmount doesn't setState on a dead component.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadLibraries = useCallback(async () => {
    const current = librariesStateRef.current;
    if (current.status === 'loading' || current.status === 'loaded') return;
    // Update the ref synchronously, not just via state. setLibrariesState is
    // async and the ref-syncing effect runs after commit, so without this a
    // second call before re-render would see stale state and fetch twice.
    librariesStateRef.current = { status: 'loading' };
    setLibrariesState({ status: 'loading' });
    try {
      const libraries = await fetchLibraries();
      if (!isMountedRef.current) return;
      if (libraries.length === 0) {
        setLibrariesState({ status: 'empty' });
      } else {
        const { labelByLibrary, libraryByLabel } = buildLabels(libraries);
        setLibrariesState({
          status: 'loaded',
          libraries,
          labelByLibrary,
          libraryByLabel,
        });
      }
    } catch {
      if (!isMountedRef.current) return;
      setLibrariesState({ status: 'error' });
    }
  }, [fetchLibraries]);

  const handleRetryLibraries = useCallback(() => {
    setLibrariesState({ status: 'idle' });
    void loadLibraries();
  }, [loadLibraries]);

  // Fetch the library list up front so the dropdown is ready without waiting
  // for the user to focus the picker. loadLibraries no-ops if a load is
  // already in flight or complete.
  useEffect(() => {
    void loadLibraries();
  }, [loadLibraries]);

  return (
    <Stack spacing={3}>
      <Inline spacing={2} alignY="center" alignX="left">
        <img className={styles.Logo} src={LibkeyLogo} alt="" />
        <Heading type="h4">LibKey</Heading>
      </Inline>

      <Text>
        LibKey connects you to full-text journal articles through your
        institution&apos;s library subscriptions. Link your library to get
        one-click PDF access on studies in Covidence.{' '}
        <Link
          variant="primary"
          to="https://support.covidence.org/help/how-to-setup-libkey"
          external
        >
          Learn more about LibKey
        </Link>
      </Text>

      <LibraryPicker
        initialSelection={initialSelection}
        saveSelection={saveSelection}
        librariesState={librariesState}
        onRetryLibraries={handleRetryLibraries}
      />
    </Stack>
  );
};

export { LibkeyLibrarySection };
